import {
    getVapidPublicKey,
    registerPushSubscription,
    unregisterPushSubscription,
    getSubscriptions,
    subscribeToEntity,
    subscribeToEvent,
    unsubscribe,
    getPreferences,
    updatePreferences
} from '../api/notifications';

// Check if browser supports notifications
export const checkBrowserSupport = () => {
    if (typeof window === 'undefined') return false;
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
};

export const getNotificationPermission = () => {
    if (!checkBrowserSupport()) return 'unsupported';
    return window.Notification.permission;
};

// Request notification permission
export const requestNotificationPermission = async () => {
    if (!checkBrowserSupport()) {
        return 'unsupported';
    }

    if (window.Notification.permission === 'granted') {
        return 'granted';
    }

    if (window.Notification.permission === 'denied') {
        return 'denied';
    }

    try {
        const permission = await window.Notification.requestPermission();
        return permission;
    } catch (error) {
        console.error('Error requesting notification permission:', error);
        return 'default';
    }
};

// Create push subscription
export const createPushSubscription = async () => {
    if (!checkBrowserSupport()) {
        throw new Error('Browser does not support push notifications');
    }
    
    if (window.Notification.permission !== 'granted') {
        throw new Error('Notification permission not granted');
    }
    
    try {
        // Get service worker registration
        const registration = await navigator.serviceWorker.ready;
        
        // Get VAPID public key
        const publicKey = await getVapidPublicKey();
        
        // Subscribe to push service
        const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicKey)
        });
        
        return subscription;
    } catch (error) {
        console.error('Error creating push subscription:', error);
        throw error;
    }
};

// Convert VAPID key from URL-safe base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/\-/g, '+')
        .replace(/_/g, '/');
    
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

// Ensure a browser PushSubscription exists and is persisted to the backend.
// Reuses an existing pushManager subscription when present (heals empty-DB cases).
export const ensurePushRegistration = async () => {
    if (!checkBrowserSupport()) {
        throw new Error('Browser does not support push notifications');
    }

    if (window.Notification.permission !== 'granted') {
        throw new Error('Notification permission not granted');
    }

    try {
        let subscription = await getExistingPushSubscription();
        if (!subscription) {
            subscription = await createPushSubscription();
        }

        const subscriptionObject = {
            endpoint: subscription.endpoint,
            keys: {
                p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
                auth: arrayBufferToBase64(subscription.getKey('auth'))
            }
        };

        await registerPushSubscription(subscriptionObject);
        return subscription;
    } catch (error) {
        console.error('Error ensuring push registration:', error);
        throw error;
    }
};

// Register push subscription with backend (creates or reuses local subscription)
export const registerPushSubscriptionWithBackend = async () => {
    return ensurePushRegistration();
};

// Convert ArrayBuffer to base64
function arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
}

// Get existing push subscription
export const getExistingPushSubscription = async () => {
    if (!checkBrowserSupport()) {
        return null;
    }
    
    try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        return subscription;
    } catch (error) {
        console.error('Error getting existing push subscription:', error);
        return null;
    }
};

// Unregister this device's push subscription with the backend and the browser
export const unregisterPushSubscriptionForDevice = async () => {
    const subscription = await getExistingPushSubscription();
    if (!subscription) return false;

    await unregisterPushSubscription(subscription.endpoint);
    try {
        await subscription.unsubscribe();
    } catch (error) {
        // Backend no longer targets this device; local unsubscribe failure is non-fatal
        console.error('Error unsubscribing push subscription locally:', error);
    }
    return true;
};

// Export API functions
export {
    getVapidPublicKey,
    registerPushSubscription,
    unregisterPushSubscription,
    getSubscriptions,
    subscribeToEntity,
    subscribeToEvent,
    unsubscribe,
    getPreferences,
    updatePreferences
};










